import React, { useState, useEffect, useReducer, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';

import Loader from '../../../../../../components/Loaders/Loader';
import ModalPreloader from '../../../../../../components/Modal/Preloader';
import WrapperAccessFields from './WrapperComponents/AccessFields';

import { serviceServicesGetServiceForm, serviceServicesGetProviderForm } from '../../../../../../services/endpoints/services';
import { editService } from '../../../reducer';
import { getModalName, getPayloadForm, getSubmitFunction } from './helper';
import { editServicesReducer, editServicesInitState } from './reducer';
import * as editServiceTypes from './reducer/constants';
import { delay } from '../../../../../../utils/helper';
import { getErrorMessage } from '../../../helpers/getters';
import { getCategoriesList } from '../../../../../../redux/actions/services';
import filterCancel from './components/FieldsOptions/filters/cancel';

const ModalEditService = ({ modalData, serviceDispatch, onClose }) => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState(null);
  const [serviceData, dispatchServiceData] = useReducer(editServicesReducer, editServicesInitState);
  const { init, isLoading, error, success, form, providerForm, isLoadingProvider, isLoadingProviderRefill } = serviceData;

  const modalTitle = useMemo(() => getModalName(modal), [modal]);

  const scrollTopModal = () => {
    const modal = document?.querySelector('.modal');
    if (modal?.scrollTop) {
      modal.scrollTop = 0;
    }
  };

  const addServiceForm = async (response) => {
    if (response?.data) {
      const { data } = response;
      await delay(300);
      return dispatchServiceData({ type: editServiceTypes.ADD_SERVICES_FORM_SUCCESS, data });
    }
  };

  const getProviderForm = async (providerId) => {
    try {
      const response = await serviceServicesGetProviderForm(providerId);
      if (response?.data) {
        const { data } = response;
        await delay(500);
        return data;
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      await delay(500);
      return Promise.reject(errorMessage);
    }
  };

  const editServiceForm = async (response) => {
    if (response?.data) {
      const { data } = response;

      let providerForm = null;
      let providerFormRefill = null;
      let errorProvider = null;
      let errorProviderRefill = null;
      const form = _.cloneDeep(data);

      const providerId = data?.provider_id;
      const refillProviderId = data?.refill?.refill_provider_id;

      if (providerId) {
        try {
          const providerResponse = await getProviderForm(providerId);
          if (providerResponse) {
            providerForm = providerResponse;
          }
        } catch (error) {
          form.provider_has_error = true;
          errorProvider = getErrorMessage(error);
        }
      }

      if (refillProviderId) {
        try {
          const providerResponse = await getProviderForm(refillProviderId);
          if (providerResponse) {
            providerFormRefill = providerResponse;
          }
        } catch (error) {
          if (form?.refill) {
            form.refill.refill_provider_has_error = true;
          }
          errorProviderRefill = getErrorMessage(error);
        }
      }

      await delay(300);
      return dispatchServiceData({
        type: editServiceTypes.EDIT_SERVICES_FORM_SUCCESS,
        form,
        providerForm,
        providerFormRefill,
        errorProvider,
        errorProviderRefill,
      });
    }
  };

  const getServiceForm = async (serviceId) => {
    try {
      const response = await serviceServicesGetServiceForm(serviceId);
      if (serviceId) {
        await editServiceForm(response);
      } else {
        await addServiceForm(response);
      }
    } catch (error) {
      await delay(500);
      const errorMessage = getErrorMessage(error);
      dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_ERROR, error: errorMessage });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_SUBMIT });
    const payload = await getPayloadForm(form);
    const serviceSubmitFunction = getSubmitFunction(modal, form, providerForm);
    const serviceId = modal?.id;

    try {
      const response = await serviceSubmitFunction(payload, serviceId);
      if (response?.data?.id) {
        const newId = response.data.id;
        await dispatch(getCategoriesList());
        dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_SUBMIT_SUCCESS });
        await delay(500);
        return await serviceDispatch(editService(newId));
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      await delay(500);
      await scrollTopModal();
      return dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_ERROR, error: errorMessage });
    }
  };

  useEffect(() => {
    if (form?.provider_id) {
      const { provider_id } = form;
      if (isLoadingProvider) {
        getProviderForm(provider_id)
          .then((data) => {
            dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_PROVIDER_FORM_SUCCESS, data });
            /**
             * Если провайдер или сервис
             * поддерживает Cancel
             * сразу включаем
             */
            const isCancel = filterCancel(form, data);
            if (isCancel) {
              dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_FORM_CHANGE_CANCEL, value: true })
            }
          })
          .catch((errorMessage) => {
            dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_PROVIDER_FORM_ERROR, error: errorMessage });
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isLoadingProvider]);

  useEffect(() => {
    if (form?.refill?.refill_provider_id) {
      const {
        refill: { refill_provider_id },
      } = form;
      if (isLoadingProviderRefill) {
        getProviderForm(refill_provider_id)
          .then((data) => {
            dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_PROVIDER_FORM_REFILL_SUCCESS, data });
          })
          .catch((errorMessage) => {
            dispatchServiceData({ type: editServiceTypes.EDIT_SERVICES_PROVIDER_FORM_REFILL_ERROR, error: errorMessage });
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isLoadingProviderRefill]);

  useEffect(() => {
    setModal(modalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (modal === modalData) {
      const id = modal?.id ? modal.id : null;
      getServiceForm(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  return (
    <>
      <Modal.Header closeButton={init && !isLoading}>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <ModalPreloader isLoading={!init}>
        <form onSubmit={onSubmit} id="service-form">
          <Modal.Body>
            {isLoading ? <Loader success={success} /> : null}
            {error ? (
              <div
                className={classNames('alert alert-danger', {
                  'alert-single': false,
                })}
              >
                {error}
              </div>
            ) : null}
            {form ? <WrapperAccessFields dispatchServiceData={dispatchServiceData} serviceData={serviceData} modal={modal} /> : null}
          </Modal.Body>
          <Modal.Footer className="custom__modal-footer">
            {form ? (
              <Button disabled={isLoading} bsStyle="primary" type="submit">
                Save changes
              </Button>
            ) : null}
            <Button disabled={isLoading} onClick={onClose}>
              Close
            </Button>
          </Modal.Footer>
        </form>
      </ModalPreloader>
    </>
  );
};

ModalEditService.propTypes = {
  modalData: PropTypes.any,
  serviceDispatch: PropTypes.func,
  onClose: PropTypes.func,
};

export default ModalEditService;
